import { useEffect, useState, type FormEvent } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { AuditLog } from '../types';
import { formatTime } from '../utils';
import { Button, EmptyBlock, LoadingBlock, PageHeader, Panel, TextInput } from '../components/ui';

export function AuditPage() {
  const { permissions, user } = useAuth();
  const { toast } = useUi();
  const [logs, setLogs] = useState<AuditLog[] | null>(null);
  const [query, setQuery] = useState('');
  const [action, setAction] = useState('');

  const load = async () => {
    if (!permissions.canViewAudit) return;
    const params = new URLSearchParams();
    if (query) params.set('query', query);
    if (action) params.set('action', action);
    const data = await api<{ logs: AuditLog[] }>('/api/admin/audit?' + params.toString());
    setLogs(data.logs);
  };

  useEffect(() => {
    void load().catch(() => toast('审计日志加载失败', 'error'));
  }, [permissions.canViewAudit, user?.id]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    void load().catch(() => toast('查询失败', 'error'));
  };

  if (!permissions.canViewAudit) return <EmptyBlock title="无权访问" description="审计日志仅对管理员开放。" />;

  return (
    <>
      <PageHeader title="审计日志" description="记录登录、资金变动、权限变更和业务操作，便于追溯。" action={<Button onClick={() => void load()}><RefreshCw size={16} /> 刷新</Button>} />
      <Panel className="bottom-gap">
        <form className="filter-bar" onSubmit={search}>
          <label><Search size={16} /><TextInput value={query} onChange={event => setQuery(event.target.value)} placeholder="操作者、目标 ID 或详情" /></label>
          <TextInput value={action} onChange={event => setAction(event.target.value)} placeholder="操作类型，例：admin.password" />
          <Button variant="primary" type="submit">查询</Button>
        </form>
      </Panel>
      <Panel title={'日志记录' + (logs ? ' · ' + logs.length : '')} eyebrow="AUDIT TRAIL">
        {!logs ? <LoadingBlock /> : logs.length === 0 ? <EmptyBlock title="没有匹配的日志" /> : (
          <div className="audit-list">
            {logs.map(log => (
              <article key={log.id}>
                <div className="audit-time"><strong>{formatTime(log.createdAt)}</strong><small>{log.ip || '未知 IP'}</small></div>
                <div className="audit-main">
                  <div><span className="action-code">{log.action}</span><strong>{log.actorName}</strong></div>
                  <p>{log.detail}</p>
                  {log.targetId && <small>目标：{log.targetType || 'unknown'} / {log.targetId}</small>}
                </div>
              </article>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}
